import { useState, useCallback, useEffect, useRef } from 'react'
import req from '@sportninja/common/api/request'
import {
  buildDivisionTargetsBody,
  parseDivisionTargetsResponse,
  type SavedDivisionTargetRow,
} from '../components/matchupMatrixHelpers'

/**
 * Hook result interface
 */
interface UseSchedulerDivisionTargetsResult {
  /** Saved per-division targets from GET /division-targets */
  targets: SavedDivisionTargetRow[]
  /** Loading state */
  loading: boolean
  /** Error state */
  error: Error | null
  /** Refetch targets (returns fresh data for direct use) */
  refetch: () => Promise<SavedDivisionTargetRow[]>
  /**
   * Reset the given divisions to the season-filling default
   * (`fill_feasible_slots`, no explicit target) — the same write the modal
   * makes when its input is cleared. Used by the constraints list's
   * toggle-off / delete affordance on the Games Per Team row. Refetches on
   * success so the synthetic row drops away.
   */
  clearTargets: (divisionUids: string[]) => Promise<void>
}

/**
 * Custom hook for reading the canonical per-division scheduler targets
 * (the `scheduler_division_targets` table behind GET
 * `/scheduler/flights/{flightId}/division-targets`).
 *
 * The Games Per Team modal writes these rows; the constraints list reads
 * them through this hook to surface a single "Games Per Team" summary row.
 * Mirrors the fetch shape of {@link useSchedulerConstraints} — counter-guarded
 * so a stale response can't clobber the latest, clears to `[]` when there's
 * no flight.
 *
 * @param flightId - The scheduler flight ID to read targets for
 */
export const useSchedulerDivisionTargets = (
  flightId: string | null
): UseSchedulerDivisionTargetsResult => {
  const [targets, setTargets] = useState<SavedDivisionTargetRow[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  // Counter to ignore stale fetch responses when multiple fetches are in flight
  const fetchCounterRef = useRef(0)

  /**
   * Fetch all saved targets for the flight.
   * Uses a counter to discard stale responses when multiple fetches overlap.
   */
  const fetchTargets = useCallback(async () => {
    if (!flightId) {
      setTargets([])
      return []
    }

    const thisCounter = ++fetchCounterRef.current
    setLoading(true)
    setError(null)

    try {
      const { data } = await req(
        `/scheduler/flights/${flightId}/division-targets`
      )
      // Unwrap + validate the `{ data: [...] }` envelope into saved rows.
      const result = parseDivisionTargetsResponse(data)
      // Only apply state if this is still the latest fetch
      if (thisCounter === fetchCounterRef.current) {
        setTargets(result)
      }
      return result
    } catch (err: unknown) {
      if (thisCounter === fetchCounterRef.current) {
        setError(
          err instanceof Error
            ? err
            : new Error('An error occurred while fetching division targets')
        )
        setTargets([])
      }
      return []
    } finally {
      if (thisCounter === fetchCounterRef.current) {
        setLoading(false)
      }
    }
  }, [flightId])

  /**
   * Reset the given divisions to `fill_feasible_slots` (no explicit target).
   * Mirrors the modal's own "cleared input" save, so a target removed from the
   * list lands in exactly the same state as one removed from the modal.
   */
  const clearTargets = useCallback(
    async (divisionUids: string[]) => {
      if (!flightId || divisionUids.length === 0) {
        return
      }
      await req(`/scheduler/flights/${flightId}/division-targets`, {
        method: 'POST',
        body: JSON.stringify({
          targets: buildDivisionTargetsBody(
            divisionUids.map((divisionId) => ({
              divisionId,
              target: {
                target_mode: 'fill_feasible_slots',
                target_number: null,
              },
            }))
          ),
          cleanup_legacy_pairs: true,
        }),
      })
      await fetchTargets()
    },
    [flightId, fetchTargets]
  )

  // Fetch targets when flightId changes
  useEffect(() => {
    if (flightId) {
      fetchTargets()
    } else {
      setTargets([])
    }
  }, [flightId]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    targets,
    loading,
    error,
    refetch: fetchTargets,
    clearTargets,
  }
}

export default useSchedulerDivisionTargets
